// src/pages/Admin/HomeContentManager.jsx
import React, { useState, useEffect, useCallback } from "react";
import { doc, getDoc, setDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../services/firebase";

// ─── Constants ────────────────────────────────────────────────────────────────
const GALLERY_DOC = "home_content";
const GALLERY_COLLECTION = "site_config";
const MAX_GALLERY_IMAGES = 8;
const FEATURED_LIMIT = 3;

const SPAN_OPTIONS = [{ value: "normal", label: "Normal" }, { value: "tall", label: "Tall (spans 2 rows)" }];
const POSITION_OPTIONS = [{ value: "center top", label: "Top (Prioritize Faces/Hair)" }, { value: "center center", label: "Center (Prioritize Middle)" }, { value: "center bottom", label: "Bottom (Prioritize Details/Feet)" }];
const LAYOUT_OPTIONS = [{ value: "hero", label: "Hero", description: "First image spans the full page width at 85% viewport height — bold, editorial impact.", icon: "⬛" }, { value: "grid", label: "Side by Side", description: "Multiple images arranged in a responsive grid spanning the full page width.", icon: "⬜⬜" }];
const EMPTY_IMAGE = { src: "", alt: "", caption: "", span: "normal", position: "center top" };

// ─── Sub-components ───────────────────────────────────────────────────────────
const SectionHeading = ({ title, subtitle }) => (
    <div style={{ marginBottom: "28px" }}>
        <h3 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "1.5rem", fontWeight: 500, color: "#f5f0e8", letterSpacing: "2px", margin: "0 0 6px" }}>{title}</h3>
        <p style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.72rem", letterSpacing: "1px", color: "rgba(245,240,232,0.4)", margin: 0 }}>{subtitle}</p>
        <div style={{ width: 40, height: 1, background: "#d4af37", opacity: 0.4, marginTop: 12 }} />
    </div>
);

const GoldButton = ({ onClick, children, disabled, variant = "outline", style = {} }) => {
    const [hovered, setHovered] = useState(false);
    const isGhost = variant === "outline";
    return (
        <button onClick={onClick} disabled={disabled} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{ background: isGhost ? (hovered ? "#d4af37" : "transparent") : (hovered ? "#b8960a" : "#d4af37"), border: "1px solid #d4af37", color: isGhost ? (hovered ? "#000" : "#d4af37") : "#000", fontFamily: "'Montserrat', sans-serif", fontSize: "0.68rem", fontWeight: 700, letterSpacing: "2.5px", textTransform: "uppercase", padding: "9px 20px", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1, transition: "all 0.2s ease", borderRadius: 0, ...style }}>
            {children}
        </button>
    );
};

const DangerButton = ({ onClick, children }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <button onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{ background: "transparent", border: "1px solid #e57373", color: hovered ? "#000" : "#e57373", backgroundColor: hovered ? "#e57373" : "transparent", fontFamily: "'Montserrat', sans-serif", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", padding: "7px 14px", cursor: "pointer", transition: "all 0.2s ease", borderRadius: 0 }}>
            {children}
        </button>
    );
};

const FieldLabel = ({ children }) => <label style={{ display: "block", fontFamily: "'Montserrat', sans-serif", fontSize: "0.62rem", fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase", color: "rgba(245,240,232,0.45)", marginBottom: 6 }}>{children}</label>;

const StyledInput = ({ value, onChange, placeholder, type = "text", min }) => (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} min={min} style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(212,175,55,0.25)", color: "#f5f0e8", fontFamily: "'Montserrat', sans-serif", fontSize: "0.8rem", padding: "10px 14px", borderRadius: 0, outline: "none", boxSizing: "border-box" }} />
);

const StyledSelect = ({ value, onChange, options }) => (
    <select value={value} onChange={onChange} style={{ width: "100%", background: "#111", border: "1px solid rgba(212,175,55,0.25)", color: "#f5f0e8", fontFamily: "'Montserrat', sans-serif", fontSize: "0.8rem", padding: "10px 14px", borderRadius: 0, outline: "none", cursor: "pointer" }}>
        {options.map(o => <option key={o.value} value={o.value} style={{ background: "#111" }}>{o.label}</option>)}
    </select>
);

const LayoutModeSelector = ({ value, onChange }) => (
    <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
        {LAYOUT_OPTIONS.map(opt => {
            const selected = value === opt.value;
            return (
                <div key={opt.value} onClick={() => onChange(opt.value)} style={{ flex: 1, padding: "18px 20px", border: `1px solid ${selected ? "#d4af37" : "rgba(212,175,55,0.2)"}`, background: selected ? "rgba(212,175,55,0.07)" : "rgba(255,255,255,0.02)", cursor: "pointer", transition: "all 0.2s ease", userSelect: "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                        <span style={{ fontSize: "1.1rem" }}>{opt.icon}</span>
                        <span style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "2.5px", textTransform: "uppercase", color: selected ? "#d4af37" : "#f5f0e8" }}>{opt.label}</span>
                        {selected && <span style={{ marginLeft: "auto", width: 16, height: 16, background: "#d4af37", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.55rem", color: "#000", fontWeight: 700 }}>✓</span>}
                    </div>
                    <p style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.65rem", letterSpacing: "0.5px", color: "rgba(245,240,232,0.4)", margin: 0, lineHeight: 1.6 }}>{opt.description}</p>
                </div>
            );
        })}
    </div>
);

const reorderBtnStyle = (disabled) => ({ background: "transparent", border: "1px solid rgba(212,175,55,0.3)", color: disabled ? "rgba(212,175,55,0.2)" : "rgba(212,175,55,0.7)", fontFamily: "'Montserrat', sans-serif", fontSize: "0.75rem", padding: "5px 10px", cursor: disabled ? "not-allowed" : "pointer", borderRadius: 0, transition: "all 0.2s ease" });

const GalleryImageCard = ({ image, index, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) => {
    const [expanded, setExpanded] = useState(false);
    return (
        <div style={{ border: "1px solid rgba(212,175,55,0.2)", background: "rgba(255,255,255,0.02)", marginBottom: 10 }}>
            <div onClick={() => setExpanded(v => !v)} style={{ display: "flex", alignItems: "center", padding: "12px 16px", cursor: "pointer", gap: 12, userSelect: "none" }}>
                {image.src ? <img src={image.src} alt={image.alt || "preview"} style={{ width: 48, height: 56, objectFit: "cover", objectPosition: image.position || "center top", flexShrink: 0, border: "1px solid rgba(212,175,55,0.2)" }} onError={e => { e.target.style.display = "none"; }} /> : <div style={{ width: 48, height: 56, flexShrink: 0, background: "rgba(255,255,255,0.04)", border: "1px dashed rgba(212,175,55,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem" }}>📷</div>}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontFamily: "'Montserrat', sans-serif", fontSize: "0.78rem", fontWeight: 600, color: "#f5f0e8", letterSpacing: "1px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{image.caption || `Image ${index + 1}`}</p>
                    <p style={{ margin: "2px 0 0", fontFamily: "'Montserrat', sans-serif", fontSize: "0.62rem", color: "rgba(212,175,55,0.5)", letterSpacing: "1px", textTransform: "uppercase" }}>{image.span === "tall" ? "Tall" : "Normal"} · {image.src ? "Image set" : "No image"}</p>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    <button onClick={onMoveUp} disabled={isFirst} style={reorderBtnStyle(isFirst)} title="Move up">↑</button>
                    <button onClick={onMoveDown} disabled={isLast} style={reorderBtnStyle(isLast)} title="Move down">↓</button>
                    <DangerButton onClick={onRemove}>✕</DangerButton>
                </div>
                <span style={{ color: "rgba(212,175,55,0.4)", fontSize: "0.7rem", marginLeft: 4 }}>{expanded ? "▲" : "▼"}</span>
            </div>
            {expanded && (
                <div style={{ padding: "16px", borderTop: "1px solid rgba(212,175,55,0.1)", display: "grid", gap: 14 }}>
                    <div><FieldLabel>Image URL *</FieldLabel><StyledInput value={image.src} onChange={e => onChange({ ...image, src: e.target.value })} placeholder="https://example.com/model-photo.jpg" /></div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                        <div><FieldLabel>Caption (optional)</FieldLabel><StyledInput value={image.caption} onChange={e => onChange({ ...image, caption: e.target.value })} placeholder="e.g. Signature Bundles" /></div>
                        <div><FieldLabel>Alt Text</FieldLabel><StyledInput value={image.alt} onChange={e => onChange({ ...image, alt: e.target.value })} placeholder="Describe the image" /></div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                        <div><FieldLabel>Image Focal Point</FieldLabel><StyledSelect value={image.position || "center top"} onChange={e => onChange({ ...image, position: e.target.value })} options={POSITION_OPTIONS} /></div>
                        <div><FieldLabel>Card Size (Grid mode only)</FieldLabel><StyledSelect value={image.span || "normal"} onChange={e => onChange({ ...image, span: e.target.value })} options={SPAN_OPTIONS} /></div>
                    </div>
                </div>
            )}
        </div>
    );
};

const FeaturedProductCard = ({ product, isSelected, onToggle }) => (
    <div onClick={onToggle} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", border: `1px solid ${isSelected ? "#d4af37" : "rgba(212,175,55,0.15)"}`, background: isSelected ? "rgba(212,175,55,0.06)" : "rgba(255,255,255,0.02)", cursor: "pointer", transition: "all 0.2s ease", marginBottom: 8, userSelect: "none" }}>
        {product.images?.[0] ? <img src={product.images[0]} alt={product.name} style={{ width: 44, height: 52, objectFit: "cover", objectPosition: "top center", flexShrink: 0, border: "1px solid rgba(212,175,55,0.2)" }} onError={e => { e.target.style.display = "none"; }} /> : <div style={{ width: 44, height: 52, flexShrink: 0, background: "rgba(255,255,255,0.04)", border: "1px dashed rgba(212,175,55,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem" }}>📦</div>}
        <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontFamily: "'Montserrat', sans-serif", fontSize: "0.8rem", fontWeight: 600, color: "#f5f0e8", letterSpacing: "0.5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{product.name}</p>
            <p style={{ margin: "3px 0 0", fontFamily: "'Montserrat', sans-serif", fontSize: "0.65rem", color: "rgba(212,175,55,0.55)", letterSpacing: "1px", textTransform: "uppercase" }}>{product.category} · £{product.price?.toFixed(2)}</p>
        </div>
        <div style={{ width: 20, height: 20, border: `1px solid ${isSelected ? "#d4af37" : "rgba(212,175,55,0.3)"}`, background: isSelected ? "#d4af37" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.2s ease" }}>
            {isSelected && <span style={{ color: "#000", fontSize: "0.7rem", fontWeight: 700 }}>✓</span>}
        </div>
    </div>
);

// ─── Main Component ────────────────────────────────────────────────────────────

const HomeContentManager = ({ setMessage }) => {

    const [announcementData, setAnnouncementData] = useState({
        isActive: false,
        text: 'Grand Opening: Enjoy £30 off all orders over £250 with code',
        highlightText: 'LAUNCH30',
        discountAmount: 30, // Default £30
        minimumSpend: 250   // Default £250
    });
    const [announcementSaving, setAnnouncementSaving] = useState(false);
    const [announcementSuccess, setAnnouncementSuccess] = useState(false);

    const [galleryLayoutMode, setGalleryLayoutMode] = useState("grid");
    const [layoutSaving, setLayoutSaving] = useState(false);
    const [layoutSuccess, setLayoutSuccess] = useState(false);

    const [galleryImages, setGalleryImages] = useState([]);
    const [galleryLoading, setGalleryLoading] = useState(true);
    const [gallerySaving, setGallerySaving] = useState(false);
    const [gallerySuccess, setGallerySuccess] = useState(false);

    const [allProducts, setAllProducts] = useState([]);
    const [selectedFeaturedIds, setSelectedFeaturedIds] = useState([]);
    const [productsLoading, setProductsLoading] = useState(true);
    const [featuredSaving, setFeaturedSaving] = useState(false);
    const [featuredSuccess, setFeaturedSuccess] = useState(false);

    const [bannerData, setBannerData] = useState({
        image: '',
        eyebrow: 'The Premium Collection',
        title: 'Pure Madness',
        subtitle: '100% Unprocessed Raw Hair. Pure in every strand.'
    });
    const [bannerSaving, setBannerSaving] = useState(false);
    const [bannerSuccess, setBannerSuccess] = useState(false);

    useEffect(() => {
        fetchGalleryData();
        fetchProductsAndFeatured();
    }, []);

    const fetchGalleryData = async () => {
        setGalleryLoading(true);
        try {
            const ref = doc(db, GALLERY_COLLECTION, GALLERY_DOC);
            const snap = await getDoc(ref);
            if (snap.exists()) {
                const data = snap.data();
                setGalleryImages(data.galleryImages?.length ? data.galleryImages : [{ ...EMPTY_IMAGE }]);
                if (data.galleryLayoutMode) setGalleryLayoutMode(data.galleryLayoutMode);
                if (data.bannerData) setBannerData(data.bannerData);

                if (data.announcementData) {
                    setAnnouncementData({
                        ...data.announcementData,
                        // Provide fallbacks just in case older data doesn't have these fields
                        discountAmount: data.announcementData.discountAmount || 30,
                        minimumSpend: data.announcementData.minimumSpend || 250,
                        // US versions: blank (null) = code is UK-only, US visitors see the normal text
                        textUSD: data.announcementData.textUSD || '',
                        discountAmountUSD: data.announcementData.discountAmountUSD ?? null,
                        minimumSpendUSD: data.announcementData.minimumSpendUSD ?? null
                    });
                }
            } else {
                setGalleryImages([{ ...EMPTY_IMAGE }]);
            }
        } catch (err) {
            console.error("Error fetching gallery:", err);
            setMessage("Failed to load gallery images.");
        } finally {
            setGalleryLoading(false);
        }
    };

    const fetchProductsAndFeatured = async () => {
        setProductsLoading(true);
        try {
            const snapshot = await getDocs(collection(db, "products"));
            const products = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setAllProducts(products);

            const configRef = doc(db, GALLERY_COLLECTION, GALLERY_DOC);
            const configSnap = await getDoc(configRef);
            if (configSnap.exists() && configSnap.data().featuredProductIds) {
                const validIds = configSnap.data().featuredProductIds.filter(id => products.some(p => p.id === id));
                setSelectedFeaturedIds(validIds);
            }
        } catch (err) {
            console.error("Error fetching products:", err);
            setMessage("Failed to load products.");
        } finally {
            setProductsLoading(false);
        }
    };

    const saveAnnouncement = async () => {
        setAnnouncementSaving(true);
        try {
            const ref = doc(db, GALLERY_COLLECTION, GALLERY_DOC);
            await setDoc(ref, { announcementData }, { merge: true });
            setAnnouncementSuccess(true);
            setTimeout(() => setAnnouncementSuccess(false), 3000);
        } catch (err) {
            console.error("Error saving announcement:", err);
            setMessage("Failed to save announcement bar. Please try again.");
        } finally {
            setAnnouncementSaving(false);
        }
    };

    const saveLayoutMode = async () => {
        setLayoutSaving(true);
        try {
            const ref = doc(db, GALLERY_COLLECTION, GALLERY_DOC);
            await setDoc(ref, { galleryLayoutMode }, { merge: true });
            setLayoutSuccess(true);
            setTimeout(() => setLayoutSuccess(false), 3000);
        } catch (err) {
            console.error("Error saving layout mode:", err);
            setMessage("Failed to save layout mode. Please try again.");
        } finally {
            setLayoutSaving(false);
        }
    };

    const addGalleryImage = () => {
        if (galleryImages.length >= MAX_GALLERY_IMAGES) return;
        setGalleryImages(prev => [...prev, { ...EMPTY_IMAGE }]);
    };

    const updateGalleryImage = useCallback((index, updated) => {
        setGalleryImages(prev => {
            const next = [...prev];
            next[index] = updated;
            return next;
        });
    }, []);

    const removeGalleryImage = useCallback((index) => {
        setGalleryImages(prev => prev.filter((_, i) => i !== index));
    }, []);

    const moveImage = useCallback((index, direction) => {
        setGalleryImages(prev => {
            const next = [...prev];
            const swapIndex = index + direction;
            if (swapIndex < 0 || swapIndex >= next.length) return prev;
            [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
            return next;
        });
    }, []);

    const saveGallery = async () => {
        setGallerySaving(true);
        try {
            const ref = doc(db, GALLERY_COLLECTION, GALLERY_DOC);
            await setDoc(ref, { galleryImages }, { merge: true });
            setGallerySuccess(true);
            setTimeout(() => setGallerySuccess(false), 3000);
        } catch (err) {
            console.error("Error saving gallery:", err);
            setMessage("Failed to save gallery. Please try again.");
        } finally {
            setGallerySaving(false);
        }
    };

    const toggleFeatured = useCallback((productId) => {
        setSelectedFeaturedIds(prev => {
            if (prev.includes(productId)) return prev.filter(id => id !== productId);
            if (prev.length >= FEATURED_LIMIT) return prev;
            return [...prev, productId];
        });
    }, []);

    const saveFeatured = async () => {
        setFeaturedSaving(true);
        try {
            const ref = doc(db, GALLERY_COLLECTION, GALLERY_DOC);
            await setDoc(ref, { featuredProductIds: selectedFeaturedIds }, { merge: true });
            setFeaturedSuccess(true);
            setTimeout(() => setFeaturedSuccess(false), 3000);
        } catch (err) {
            console.error("Error saving featured products:", err);
            setMessage("Failed to save featured products. Please try again.");
        } finally {
            setFeaturedSaving(false);
        }
    };

    const saveBanner = async () => {
        setBannerSaving(true);
        try {
            const ref = doc(db, GALLERY_COLLECTION, GALLERY_DOC);
            await setDoc(ref, { bannerData }, { merge: true });
            setBannerSuccess(true);
            setTimeout(() => setBannerSuccess(false), 3000);
        } catch (err) {
            console.error("Error saving banner:", err);
            setMessage("Failed to save banner. Please try again.");
        } finally {
            setBannerSaving(false);
        }
    };

    const panelStyle = { background: "#0a0a0a", border: "1px solid rgba(212,175,55,0.15)", padding: "32px", marginBottom: "28px" };
    const successBanner = (visible) => visible ? <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.3)", padding: "10px 16px", marginBottom: 16, fontFamily: "'Montserrat', sans-serif", fontSize: "0.72rem", letterSpacing: "2px", textTransform: "uppercase", color: "#d4af37" }}><span>✓</span> Saved successfully</div> : null;

    return (
        <div style={{ padding: "8px 0" }}>
            <div style={{ marginBottom: 36 }}>
                <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "2rem", fontWeight: 400, color: "#f5f0e8", letterSpacing: "3px", margin: "0 0 8px" }}>Home Page Content</h2>
                <p style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.72rem", color: "rgba(245,240,232,0.35)", letterSpacing: "1px", margin: 0 }}>Manage the layout, gallery, featured products, and banners on the home page</p>
                <div style={{ width: 56, height: 1, background: "#d4af37", opacity: 0.35, marginTop: 14 }} />
            </div>

            {/* ── Top Announcement Bar & Discount Setup ── */}
            <div style={panelStyle}>
                <SectionHeading title="Top Announcement Bar & Promotions" subtitle="Manage the global alert banner and the associated discount logic." />
                {successBanner(announcementSuccess)}
                <div style={{ display: "grid", gap: 18 }}>

                    <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", color: "#f5f0e8", fontFamily: "'Montserrat', sans-serif", fontSize: "0.85rem", letterSpacing: "1px" }}>
                        <input
                            type="checkbox"
                            checked={announcementData.isActive}
                            onChange={e => setAnnouncementData({...announcementData, isActive: e.target.checked})}
                            style={{ width: 18, height: 18, accentColor: "#d4af37", cursor: "pointer" }}
                        />
                        Enable Announcement Bar & Activate Promo Code
                    </label>

                    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
                        <div>
                            <FieldLabel>Main Message Text</FieldLabel>
                            <StyledInput
                                value={announcementData.text}
                                onChange={e => setAnnouncementData({...announcementData, text: e.target.value})}
                                placeholder="Grand Opening: Enjoy £30 off all orders over £250 with code"
                            />
                        </div>
                        <div>
                            <FieldLabel>Highlighted Text / Code</FieldLabel>
                            <StyledInput
                                value={announcementData.highlightText}
                                onChange={e => setAnnouncementData({...announcementData, highlightText: e.target.value})}
                                placeholder="LAUNCH30"
                            />
                        </div>
                    </div>

                    {/* NEW: Dynamic Cart Math Configuration */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 8, padding: "16px", background: "rgba(212,175,55,0.05)", border: "1px dashed rgba(212,175,55,0.3)" }}>
                        <div>
                            <FieldLabel>Discount Amount (£)</FieldLabel>
                            <StyledInput
                                type="number"
                                min="0"
                                value={announcementData.discountAmount}
                                onChange={e => setAnnouncementData({...announcementData, discountAmount: Number(e.target.value)})}
                            />
                            <p style={{ margin: "6px 0 0", fontSize: "0.65rem", color: "rgba(245,240,232,0.4)" }}>Amount to deduct at checkout.</p>
                        </div>
                        <div>
                            <FieldLabel>Minimum Spend Requirement (£)</FieldLabel>
                            <StyledInput
                                type="number"
                                min="0"
                                value={announcementData.minimumSpend}
                                onChange={e => setAnnouncementData({...announcementData, minimumSpend: Number(e.target.value)})}
                            />
                            <p style={{ margin: "6px 0 0", fontSize: "0.65rem", color: "rgba(245,240,232,0.4)" }}>Cart subtotal required to use code.</p>
                        </div>
                    </div>

                    {/* US visitors ($ USD) */}
                    <div style={{ display: "grid", gap: 14, marginTop: 8, padding: "16px", background: "rgba(212,175,55,0.03)", border: "1px dashed rgba(212,175,55,0.2)" }}>
                        <p style={{ margin: 0, fontFamily: "'Montserrat', sans-serif", fontSize: "0.7rem", letterSpacing: "1px", color: "#d4af37" }}>
                            US VISITORS ($ USD): leave the amounts blank to make the code UK-only
                        </p>
                        <div>
                            <FieldLabel>Message Text for US Visitors (optional)</FieldLabel>
                            <StyledInput
                                value={announcementData.textUSD || ''}
                                onChange={e => setAnnouncementData({...announcementData, textUSD: e.target.value})}
                                placeholder="Grand Opening: Enjoy $40 off all orders over $300 with code"
                            />
                            <p style={{ margin: "6px 0 0", fontSize: "0.65rem", color: "rgba(245,240,232,0.4)" }}>Blank = US visitors see the main message above. The highlighted code is the same for both.</p>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                            <div>
                                <FieldLabel>Discount Amount ($)</FieldLabel>
                                <StyledInput
                                    type="number"
                                    min="0"
                                    value={announcementData.discountAmountUSD ?? ''}
                                    onChange={e => setAnnouncementData({...announcementData, discountAmountUSD: e.target.value === '' ? null : Number(e.target.value)})}
                                    placeholder="Blank = not valid in US"
                                />
                            </div>
                            <div>
                                <FieldLabel>Minimum Spend Requirement ($)</FieldLabel>
                                <StyledInput
                                    type="number"
                                    min="0"
                                    value={announcementData.minimumSpendUSD ?? ''}
                                    onChange={e => setAnnouncementData({...announcementData, minimumSpendUSD: e.target.value === '' ? null : Number(e.target.value)})}
                                    placeholder="e.g. 300"
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: 8 }}>
                        <GoldButton onClick={saveAnnouncement} disabled={announcementSaving} variant="filled">
                            {announcementSaving ? "Saving..." : "Save Announcement Bar"}
                        </GoldButton>
                    </div>
                </div>
            </div>

            {/* ── Gallery Layout Mode Panel ── */}
            <div style={panelStyle}>
                <SectionHeading title="Model Gallery Layout" subtitle="Choose how your model images are displayed on the home page." />
                {successBanner(layoutSuccess)}
                <LayoutModeSelector value={galleryLayoutMode} onChange={setGalleryLayoutMode} />
                <p style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.68rem", color: "rgba(245,240,232,0.35)", letterSpacing: "0.5px", margin: "12px 0 20px", lineHeight: 1.7 }}>
                    {galleryLayoutMode === "hero" ? "Hero mode: Only the first image in your gallery will be shown. Add a single striking photo for maximum impact." : "Side by Side mode: All gallery images are shown in a mosaic grid. Use 'Tall' card size on individual images to create visual variety."}
                </p>
                <GoldButton onClick={saveLayoutMode} disabled={layoutSaving} variant="filled">{layoutSaving ? "Saving..." : "Save Layout"}</GoldButton>
            </div>

            {/* ── Premium Banner Settings ── */}
            <div style={panelStyle}>
                <SectionHeading title="Premium Banner Settings" subtitle="Customize the full-width promotional banner displayed above the footer." />
                {successBanner(bannerSuccess)}
                <div style={{ display: "grid", gap: 14 }}>
                    <div>
                        <FieldLabel>Background Image URL</FieldLabel>
                        <StyledInput value={bannerData.image} onChange={e => setBannerData({...bannerData, image: e.target.value})} placeholder="https://example.com/banner.jpg" />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                        <div>
                            <FieldLabel>Eyebrow Text</FieldLabel>
                            <StyledInput value={bannerData.eyebrow} onChange={e => setBannerData({...bannerData, eyebrow: e.target.value})} placeholder="The Premium Collection" />
                        </div>
                        <div>
                            <FieldLabel>Main Title</FieldLabel>
                            <StyledInput value={bannerData.title} onChange={e => setBannerData({...bannerData, title: e.target.value})} placeholder="Pure Madness" />
                        </div>
                    </div>
                    <div>
                        <FieldLabel>Subtitle</FieldLabel>
                        <StyledInput value={bannerData.subtitle} onChange={e => setBannerData({...bannerData, subtitle: e.target.value})} placeholder="100% Unprocessed Raw Hair..." />
                    </div>
                    <div style={{ marginTop: 16 }}>
                        <GoldButton onClick={saveBanner} disabled={bannerSaving} variant="filled">
                            {bannerSaving ? "Saving..." : "Save Banner"}
                        </GoldButton>
                    </div>
                </div>
            </div>

            {/* ── Model Gallery Images Panel ── */}
            <div style={panelStyle}>
                <SectionHeading title="Gallery Images" subtitle={`Up to ${MAX_GALLERY_IMAGES} images · Drag the ↑↓ arrows to reorder`} />
                {galleryLoading ? (
                    <p style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.75rem", color: "rgba(245,240,232,0.35)", letterSpacing: "2px" }}>Loading...</p>
                ) : (
                    <>
                        {successBanner(gallerySuccess)}
                        {galleryImages.map((image, index) => (
                            <GalleryImageCard key={index} image={image} index={index} onChange={(updated) => updateGalleryImage(index, updated)} onRemove={() => removeGalleryImage(index)} onMoveUp={() => moveImage(index, -1)} onMoveDown={() => moveImage(index, 1)} isFirst={index === 0} isLast={index === galleryImages.length - 1} />
                        ))}
                        <div style={{ display: "flex", gap: 12, marginTop: 16, alignItems: "center" }}>
                            <GoldButton onClick={addGalleryImage} disabled={galleryImages.length >= MAX_GALLERY_IMAGES}>+ Add Image</GoldButton>
                            <GoldButton onClick={saveGallery} disabled={gallerySaving} variant="filled">{gallerySaving ? "Saving..." : "Save Gallery"}</GoldButton>
                            <span style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.62rem", color: "rgba(245,240,232,0.25)", letterSpacing: "1px", marginLeft: "auto" }}>{galleryImages.length} / {MAX_GALLERY_IMAGES}</span>
                        </div>
                    </>
                )}
            </div>

            {/* ── Featured Products Panel ── */}
            <div style={panelStyle}>
                <SectionHeading title="Featured Products" subtitle={`Select exactly ${FEATURED_LIMIT} products to feature on the home page`} />
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, padding: "10px 16px", background: "rgba(212,175,55,0.04)", border: "1px solid rgba(212,175,55,0.15)" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                        {[1, 2, 3].map(n => <div key={n} style={{ width: 10, height: 10, borderRadius: "50%", background: selectedFeaturedIds.length >= n ? "#d4af37" : "rgba(212,175,55,0.2)", transition: "background 0.2s ease" }} />)}
                    </div>
                    <span style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.68rem", letterSpacing: "2px", color: selectedFeaturedIds.length === FEATURED_LIMIT ? "#d4af37" : "rgba(245,240,232,0.4)", textTransform: "uppercase" }}>{selectedFeaturedIds.length} of {FEATURED_LIMIT} selected{selectedFeaturedIds.length === FEATURED_LIMIT && " · Ready to save"}</span>
                </div>
                {productsLoading ? (
                    <p style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.75rem", color: "rgba(245,240,232,0.35)", letterSpacing: "2px" }}>Loading products...</p>
                ) : allProducts.length === 0 ? (
                    <p style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.75rem", color: "rgba(245,240,232,0.35)", letterSpacing: "2px" }}>No products found. Add products first.</p>
                ) : (
                    <>
                        {successBanner(featuredSuccess)}
                        <div style={{ maxHeight: 480, overflowY: "auto", marginBottom: 16, paddingRight: 4 }}>
                            {allProducts.map(product => <FeaturedProductCard key={product.id} product={product} isSelected={selectedFeaturedIds.includes(product.id)} onToggle={() => toggleFeatured(product.id)} />)}
                        </div>
                        <GoldButton onClick={saveFeatured} disabled={featuredSaving || selectedFeaturedIds.length === 0} variant="filled">{featuredSaving ? "Saving..." : "Save Featured Products"}</GoldButton>
                    </>
                )}
            </div>
        </div>
    );
};

export default HomeContentManager;